import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import { MoveSeatsRequest, useMoveSeats } from 'features/subscription/hooks/use-seats';
import {
  BILLING_PROFILE_INCOMPLETE,
  PAYMENT_METHOD_MISSING,
  PlanSeats,
  planSeatsOfUser,
  quantityForMore,
  seatsToAdd,
  seatsToPay,
  utilitiesErrorCode,
} from 'features/subscription/utils/seat-pools';
import { SeatPurchaseRequest } from './add-seat-dialog';
import { planUnavailableNote } from './plan-actions';

type T = (key: string, options?: Record<string, unknown> | string) => string;

/**
 * Why these people cannot go onto this plan now, or "" when they can. Asked before anything is
 * bought, so a move that would be refused is never paid for.
 */
export const seatMoveProblem = (t: T, entry: PlanSeats, moving: number): string => {
  if (entry.awaitingPayment) return planUnavailableNote(t, entry);
  if (seatsToPay(entry, moving) === 0) return '';
  if (entry.paymentFailed) return planUnavailableNote(t, entry);
  if (entry.cancelScheduled) {
    return t('PLAN_CANCELLED_NO_SEATS', {
      plan: entry.name,
      defaultValue: '{{plan}} is cancelled, so no seats can be added. Keep the plan first.',
    });
  }
  if (!entry.canAddSeats) {
    return t('PLAN_SEATS_FIXED', {
      plan: entry.name,
      free: entry.freeThisPeriod,
      defaultValue: '{{plan}} has {{free}} seats available and a fixed number, so more cannot be added.',
    });
  }
  return '';
};

export interface PlanMember {
  userId: string;
  name: string;
}

/**
 * Puts one person or a group on a plan: checks first, then uses the seats paid for this period, and
 * asks the admin to confirm a payment only for seats beyond those. Shared by the members table and
 * the plans page, so both switch the same way. `onFreeSeat` runs once the people are seated on seats
 * already paid for, with nothing bought: a switch that has no dialog to confirm it.
 */
export const useGivePlan = (planSeats: PlanSeats[]) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutate: moveSeats, isPending } = useMoveSeats();
  const [purchase, setPurchase] = useState<{ request: SeatPurchaseRequest; move: MoveSeatsRequest } | null>(null);
  const [cardNeededFor, setCardNeededFor] = useState<string | null>(null);
  const [billingNeeded, setBillingNeeded] = useState(false);
  const [onDone, setOnDone] = useState<(() => void) | undefined>();

  const givePlan = (members: PlanMember[], entry: PlanSeats, done?: () => void, onFreeSeat?: () => void) => {
    const moving = members
      .map((member) => ({ ...member, from: planSeatsOfUser(planSeats, member.userId) }))
      .filter((person) => person.from?.code !== entry.code);
    if (moving.length === 0) return;
    const problem = seatMoveProblem(t, entry, moving.length);
    if (problem) {
      toast({ variant: 'destructive', title: t('CANNOT_SWITCH_PLAN', 'This plan cannot be given now'), description: problem });
      return;
    }
    const add = seatsToAdd(entry, moving.length);
    const pay = seatsToPay(entry, moving.length);
    const move: MoveSeatsRequest = {
      people: moving.map(({ userId, from }) => ({ userId, from })),
      to: entry,
      add,
    };
    if (pay === 0) {
      moveSeats(move, {
        onSuccess: (seated) => {
          done?.();
          if (seated && add === 0) onFreeSeat?.();
        },
      });
      return;
    }
    setOnDone(() => done);
    setPurchase({
      request: {
        entry,
        count: pay,
        quantity: quantityForMore(entry, add),
        who:
          moving.length === 1
            ? moving[0].name
            : t('PEOPLE_COUNT', { count: moving.length, defaultValue: '{{count}} people' }),
        leavesSeats: moving.some((person) => person.from),
      },
      move,
    });
  };

  const close = () => {
    setPurchase(null);
    setCardNeededFor(null);
    setBillingNeeded(false);
  };

  const confirm = (entry?: PlanSeats) => {
    if (!purchase) return;
    moveSeats({ ...purchase.move, to: entry ?? purchase.move.to }, {
      onSuccess: () => {
        close();
        onDone?.();
      },
      onError: (error) => {
        const code = utilitiesErrorCode(error);
        const subscriptionId = purchase.request.entry.pool?.subscription.subscriptionId;
        if (code === PAYMENT_METHOD_MISSING && subscriptionId) setCardNeededFor(subscriptionId);
        if (code === BILLING_PROFILE_INCOMPLETE) setBillingNeeded(true);
      },
    });
  };

  return {
    givePlan,
    isPending,
    dialog: {
      request: purchase?.request ?? null,
      cardNeededFor,
      billingNeeded,
      isPending,
      onConfirm: confirm,
      onClose: close,
    },
  };
};
